/**
 * Expanded AI-request disclosure (TMMAPP-0030) — the exact text sent when stored (TMMAPP-0044,
 * MM-Q-33) and/or a note on it (TMMAPP-0046; absence reason TMMAPP-0047), then resolved
 * coverage, labelled counts, and the collapsed raw manifest. The ids-and-counts layers stay for
 * every row.
 *
 * Another member's call in their private room or own session (TMMAPP-0148 U1 item 9; TMMAPI-0184
 * U3 `ASKED_BY_ANOTHER`) is the payer's to see by charge, time, model and outcome only: "Private:
 * only {name} can see what was asked.", and nothing else here (never an empty or "not sent" note).
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { ExpandIcon } from '@/components/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AiPayloadAbsentReason, AiRequest } from '@/api/aiRequests';
import { aiAskerName } from './aiRequestSession';
import { categoriesApi, type Category } from '@/api/categories';
import type { Payee } from '@/api/payees';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import { formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { AiSentPayload } from './AiSentPayload';

const KNOWN_COUNT_KEYS = [
  'lineCount',
  'exampleCount',
  'pnlRowCount',
  'projectionEventCount',
] as const;

type KnownCountKey = (typeof KNOWN_COUNT_KEYS)[number];

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asIdList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
}

function asId(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** `someNewCount` → "Some new count"; never drops an unrecognised key. */
export function humanizeManifestKey(key: string): string {
  const spaced = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (spaced.length === 0) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function prettyManifest(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type PayloadNoteKey =
  'payloadPending' | 'maybeSent' | 'noPayload' | 'notSent' | 'payloadRemoved' | 'askedByAnother';

const ABSENT_NOTE: Record<AiPayloadAbsentReason, PayloadNoteKey> = {
  LEGACY: 'noPayload',
  PENDING: 'payloadPending',
  NOT_SENT: 'notSent',
  // TMMAPI-0181 U6: never "not sent" — it was sent, then erased with a contributor's data.
  REMOVED: 'payloadRemoved',
  // TMMAPI-0184 U3: another member asked it; only they see what was asked.
  ASKED_BY_ANOTHER: 'askedByAnother',
};

/**
 * The note beside (or instead of) the sent text. With a payload it keys on outcome (TMMAPP-0046):
 * tmm-api stores the body immediately before sending, so a PENDING row is in flight and a FAILED
 * row may or may not have reached the provider. Without one it keys on tmm-api's
 * `outboundPayloadAbsentReason` (TMMAPP-0047) — never inferred from the outcome; a missing reason
 * falls back to the pre-disclosure note.
 */
function payloadNoteKey(
  outcome: string,
  hasPayload: boolean,
  absentReason: AiPayloadAbsentReason | null,
): PayloadNoteKey | null {
  if (!hasPayload) return absentReason === null ? 'noPayload' : ABSENT_NOTE[absentReason];
  if (outcome === 'PENDING') return 'payloadPending';
  if (outcome === 'FAILED') return 'maybeSent';
  return null;
}

function MonoId({ id }: { id: number }) {
  return (
    <Typography variant="mono" component="span">
      #{id}
    </Typography>
  );
}

export interface AiRequestDetailProps {
  request: AiRequest;
  /** When false, skip resolution fetches (list stays unblocked). */
  open: boolean;
  /** Page-cached payee directory; `null` while the page has not loaded it yet. */
  payees: Payee[] | null;
  /** Ask the page to load `payeesApi.list` once (cached for the page). */
  onNeedPayees: () => void;
}

export function AiRequestDetail({ request, open, payees, onNeedPayees }: AiRequestDetailProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { envelopes, status: envelopesStatus } = useOwnedEnvelopes();
  const manifest = asRecord(request.inputManifest);
  const envelopeId = asId(manifest.envelopeId);
  const categoryIds = asIdList(manifest.categoryIds);
  const payeeIds = asIdList(manifest.existingPayeeIds);
  const redactionVersion =
    typeof manifest.redactionVersion === 'string' || typeof manifest.redactionVersion === 'number'
      ? String(manifest.redactionVersion)
      : null;

  const needsCategories = open && envelopeId !== null && categoryIds.length > 0;
  const ownedForEnvelope =
    envelopeId !== null ? envelopes.find((row) => row.id === envelopeId) : undefined;
  const embeddedCategories =
    ownedForEnvelope?.categories != null && ownedForEnvelope.categories.length > 0
      ? ownedForEnvelope.categories
      : null;

  /** Async fetch only — keyed by envelopeId so a stale result is ignored during render. */
  const [fetchedCategories, setFetchedCategories] = useState<{
    envelopeId: number;
    rows: Category[] | 'failed';
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    if (payeeIds.length > 0) onNeedPayees();
  }, [open, onNeedPayees, payeeIds.length]);

  // Categories belong to the envelope's virtual account (TMMAPI-0145); an envelope the caller does
  // not own resolves nothing (the ids stay technical).
  const virtualAccountId = ownedForEnvelope?.virtualAccountId ?? null;
  useEffect(() => {
    if (
      !needsCategories ||
      envelopeId === null ||
      envelopesStatus === 'loading' ||
      embeddedCategories
    ) {
      return;
    }
    if (virtualAccountId === null) return;
    let cancelled = false;
    void categoriesApi
      .list(virtualAccountId)
      .then((rows) => {
        if (!cancelled) setFetchedCategories({ envelopeId, rows });
      })
      .catch(() => {
        if (!cancelled) setFetchedCategories({ envelopeId, rows: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [needsCategories, envelopeId, envelopesStatus, embeddedCategories, virtualAccountId]);

  const fetchedForEnvelope =
    fetchedCategories !== null && fetchedCategories.envelopeId === envelopeId
      ? fetchedCategories.rows
      : null;
  // An envelope the caller does not own has no virtual account to read from: unresolved.
  const unownedEnvelope = virtualAccountId === null && envelopesStatus !== 'loading';
  const categories: Category[] | 'loading' | 'failed' | null = !needsCategories
    ? null
    : embeddedCategories !== null
      ? embeddedCategories
      : fetchedForEnvelope === 'failed' || unownedEnvelope
        ? 'failed'
        : Array.isArray(fetchedForEnvelope)
          ? fetchedForEnvelope
          : 'loading';

  const hasCoverage = envelopeId !== null || categoryIds.length > 0 || payeeIds.length > 0;

  const countEntries: { key: string; value: number }[] = [];
  for (const [key, value] of Object.entries(manifest)) {
    if (key === 'excludedLineCount' || key === 'redactionVersion') continue;
    if (key === 'envelopeId' || key === 'categoryIds' || key === 'existingPayeeIds') continue;
    if (typeof value === 'number' && Number.isFinite(value)) {
      countEntries.push({ key, value });
    }
  }
  const excludedLineCount = asId(manifest.excludedLineCount);

  let envelopeLabel: ReactNode = null;
  if (envelopeId !== null) {
    if (ownedForEnvelope) {
      envelopeLabel = ownedForEnvelope.name;
    } else {
      envelopeLabel = <MonoId id={envelopeId} />;
    }
  }

  let categoryLabel: ReactNode = null;
  if (categoryIds.length > 0) {
    if (categories === 'failed') {
      categoryLabel = String(categoryIds.length);
    } else if (categories === 'loading' || categories === null) {
      categoryLabel = (
        <Stack component="span" direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
          {categoryIds.map((id) => (
            <MonoId key={id} id={id} />
          ))}
        </Stack>
      );
    } else {
      const byId = new Map(categories.map((c) => [c.id, c.name]));
      categoryLabel = categoryIds.map((id) => byId.get(id) ?? `#${id}`).join(', ');
    }
  }

  let payeeLabel: ReactNode = null;
  if (payeeIds.length > 0) {
    if (payees === null) {
      payeeLabel = (
        <Stack component="span" direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
          {payeeIds.map((id) => (
            <MonoId key={id} id={id} />
          ))}
        </Stack>
      );
    } else {
      const byId = new Map(payees.map((p) => [p.id, p]));
      payeeLabel = (
        <Stack component="span" direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
          {payeeIds.map((id) => {
            const payee = byId.get(id);
            if (!payee) return <MonoId key={id} id={id} />;
            return (
              <MuiLink key={id} component={RouterLink} to={`/me/payees/${id}`}>
                {payee.name}
              </MuiLink>
            );
          })}
        </Stack>
      );
    }
  }

  const payload =
    request.outboundPayload !== null && request.outboundPayload.length > 0
      ? request.outboundPayload
      : null;
  const payloadNote = payloadNoteKey(
    request.outcome,
    payload !== null,
    request.outboundPayloadAbsentReason,
  );

  if (request.outboundPayloadAbsentReason === 'ASKED_BY_ANOTHER') {
    return (
      <Stack spacing={1.5} sx={{ my: 1 }}>
        <Typography variant="body2" color="text.secondary" data-asked-by-another="">
          {t('aiHistory.askedByAnother', { name: aiAskerName(t, request.askedBy) })}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ my: 1 }}>
      {payload !== null && (
        <AiSentPayload
          payload={payload}
          truncated={request.outboundTruncated}
          bytes={request.outboundBytes}
        />
      )}
      {payloadNote !== null && (
        <Typography variant="body2" color="text.secondary">
          {t(`aiHistory.${payloadNote}`)}
        </Typography>
      )}

      {hasCoverage && (
        <Stack spacing={0.75}>
          {envelopeId !== null && (
            <Typography variant="body2">
              {t('aiHistory.coverage.envelope')}: {envelopeLabel}
            </Typography>
          )}
          {categoryIds.length > 0 && (
            <Typography variant="body2" component="div">
              {t('aiHistory.coverage.categories')}: {categoryLabel}
            </Typography>
          )}
          {payeeIds.length > 0 && (
            <Typography variant="body2" component="div">
              {t('aiHistory.coverage.payees')}: {payeeLabel}
            </Typography>
          )}
        </Stack>
      )}

      {(countEntries.length > 0 || excludedLineCount !== null) && (
        <Stack spacing={0.5}>
          {countEntries.map(({ key, value }) => {
            const known = (KNOWN_COUNT_KEYS as readonly string[]).includes(key);
            const label = known
              ? t(`aiHistory.key.${key as KnownCountKey}`)
              : humanizeManifestKey(key);
            return (
              <Typography key={key} variant="body2">
                {label}: <TabularText variant="body2">{formatNumber(value, locale)}</TabularText>
              </Typography>
            );
          })}
          {excludedLineCount !== null && (
            <Typography variant="body2">
              {t('aiHistory.excludedNote', { count: excludedLineCount })}
            </Typography>
          )}
        </Stack>
      )}

      <Accordion
        disableGutters
        elevation={0}
        sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="body2">{t('aiHistory.technical')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {redactionVersion !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('aiHistory.redactionVersion', { v: redactionVersion })}
            </Typography>
          )}
          <Box
            component="code"
            role="group"
            aria-label={t('aiHistory.technical')}
            sx={{
              display: 'block',
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              overflowX: 'auto',
            }}
          >
            <Box component="pre" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre' }}>
              <Typography variant="mono" component="span" sx={{ whiteSpace: 'pre' }}>
                {prettyManifest(request.inputManifest)}
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
